// libs
import React, { cloneElement } from 'react';
import isEmpty from 'lodash/isEmpty';

// components
import SomethingWentWrong from 'shared/components/SomethingWentWrong';

// helpers
import { log } from 'shared/helpers/newRelic';

// constants
import { ERROR_TAGS, TAG_IDENTIFIER } from 'shared/helpers/newRelic/constants';

// defs
import { ErrorBoundaryProps, ErrorBoundaryState } from './types';

const initialState = { error: null, hasError: false };

class ErrorBoundary extends React.PureComponent<ErrorBoundaryProps, ErrorBoundaryState> {
  public static defaultProps = {
    children: null,
    validateProps: () => true,
    onFailure: () => {
      /* do nothing */
    },
    fallbackRenderProps: {},
    FallbackComponent: null,
  };
  public state = initialState;

  public static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  public componentDidCatch(error: any) {
    log({
      errTitle: 'Error in Custom Error Boundary',
      error,
      tags: { [TAG_IDENTIFIER.ERROR]: ERROR_TAGS.DEFAULT },
    });
    this.props.onFailure(error);
    this.setState({ error, hasError: true });
  }

  public render() {
    const { error } = this.state;
    const { fallbackRenderProps, FallbackComponent, children, validateProps } = this.props;
    const { props } = children;

    if (error !== null || !validateProps(props)) {
      if (FallbackComponent) {
        return FallbackComponent;
      } else if (!isEmpty(fallbackRenderProps)) {
        return cloneElement(children, fallbackRenderProps);
      } else {
        // avoid falling back to this, might impact the entire layout
        return SomethingWentWrong;
      }
    }

    return children;
  }
}

export default ErrorBoundary;
